// "Use my existing MCPs" (David, 16 Oct): the board's chat on the user's computer uses the MCP servers their own
// Claude Code or Codex already has there (email, calendars, local tools), with the sign-ins kept there, as well
// as the board's. Off until they turn it on, for each agent. A project folder brings that project's servers too.
// Shown on Claude Code's and Codex's pages in Connect and in the chat's menu; the runner checks the folder.

import { useId, useState } from "react";
import type { OwnMcps } from "../../shared/types";
import { Switch } from "../chrome/ui";
import { api } from "../lib/api";
import { cx } from "../lib/util";
import { set, toast, useStore } from "../state/store";
import { Why } from "./Why";

/** The runner that first runs a chat with the user's own servers. */
const NEEDS = [0, 16, 0];

/** Whether a runner's version is older than NEEDS; one it cannot read counts as older. */
function older(v: string | null | undefined): boolean {
  const p = /^\d+\.\d+\.\d+$/.test(v ?? "") ? v!.split(".").map(Number) : [-1, -1, -1];
  for (let i = 0; i < 3; i++) if (p[i] !== NEEDS[i]) return p[i] < NEEDS[i];
  return false;
}

export function OwnMcpsSetting({ agent, compact = false, menu = false, job = false }: { agent: "claude" | "codex"; compact?: boolean; menu?: boolean; job?: boolean }) {
  const name = agent === "codex" ? "Codex" : "Claude Code";
  const runner = useStore((s) => s.runner);
  const now: OwnMcps = runner?.ownMcps?.[agent] ?? { on: false, project: null };
  const [busy, setBusy] = useState(false);
  const switchId = useId();
  const main = runner?.computers?.find((c) => c.main) ?? null;
  const host = runner?.host ?? main?.host ?? "your computer";
  const folders = runner?.folders ?? [];
  const old = !!runner?.online && older(runner.version);
  const save = async (next: OwnMcps) => {
    setBusy(true);
    try {
      const r = await api<{ ownMcps: NonNullable<typeof runner>["ownMcps"] }>("/api/chat/own-mcps", { method: "PUT", body: { agent, on: next.on, project: next.project } });
      if (runner) set({ runner: { ...runner, ownMcps: r.ownMcps ?? undefined } });
    } catch (e) {
      toast({ level: "warn", title: "That did not change", body: e instanceof Error ? e.message : "Try again." });
    } finally {
      setBusy(false);
    }
  };
  // the chat's menu (Fixes 8, David: "far fewer words"): a label and its switch, the project a short picker, what they
  // do behind a ?, and one short line only where it keeps a choice safe
  if (menu)
    return (
      <div className="own-mcps-menu" aria-busy={busy}>
        <div className="chat-set">
          <label className="chat-set-name" htmlFor={switchId}>
            Use my existing MCPs
            <Why text={now.on ? `The chat uses the MCP servers ${name} has on ${host}, as well as the board's. They run and sign in there, and a call you haven't allowed yet asks you first.` : `Lets the chat use the MCP servers ${name} already has on ${host}, like your email or calendar. They run and sign in there, never on the board.`} />
          </label>
          <button type="button" id={switchId} role="switch" aria-checked={now.on} aria-label="Use my existing MCPs" className={cx("switch", now.on && "is-on")} onClick={() => !busy && void save({ ...now, on: !now.on })}>
            <i />
          </button>
        </div>
        {now.on && (
          <>
            <label className="chat-set">
              <span className="chat-set-name">
                Project
                <Why text={folders.length ? "A project brings its own MCP servers and settings too. Pick only one you trust." : "A project brings its own MCP servers too. Add its folder in Connect, Project folders, then pick it here."} />
              </span>
              <select value={now.project ?? ""} disabled={busy} aria-label="Project" onChange={(e) => void save({ ...now, project: e.target.value || null })}>
                <option value="">None</option>
                {folders.map((f) => (
                  <option key={f.path} value={f.path}>
                    {f.name}
                  </option>
                ))}
              </select>
            </label>
            {now.project && <p className="chat-set-sub">Only a project you trust: its servers run too</p>}
            {old && <p className="chat-set-sub is-warn">Updates itself on {host} in a few minutes</p>}
          </>
        )}
      </div>
    );
  return (
    <div className={compact ? "own-mcps is-compact" : "own-mcps"} aria-busy={busy}>
      <Switch label="Use my existing MCPs" checked={now.on} onChange={(on) => void save({ ...now, on })} />
      <p className="own-mcps-sub">
        {job
          ? now.on
            ? `Its runs use the MCP servers ${name} has on ${host}, like your email, as the chat does.`
            : `Turn this on for its runs to use the MCP servers ${name} has on ${host}, like your email. The chat uses them too.`
          : now.on
            ? `The chat uses the MCP servers ${name} has on ${host}, as well as the board's. They run and sign in there. A call you haven't already allowed asks you in the chat first.`
            : `Let the chat use the MCP servers ${name} already has on ${host}, like your email or calendar. They run and sign in there, never on the board.`}
      </p>
      {now.on && (
        <label className="own-mcps-project">
          <span>Project</span>
          <select value={now.project ?? ""} disabled={busy} onChange={(e) => void save({ ...now, project: e.target.value || null })}>
            <option value="">{compact ? "None" : `None: ${host}'s own servers only`}</option>
            {folders.map((f) => (
              <option key={f.path} value={f.path}>
                {f.name}
              </option>
            ))}
          </select>
        </label>
      )}
      {now.on && now.project && <p className="own-mcps-sub">Pick only a project you trust: its own settings and servers run too.</p>}
      {now.on && !folders.length && !compact && <p className="own-mcps-sub">To bring a project's own servers too, add its folder in Project folders, then pick it here.</p>}
      {now.on && old && <p className="own-mcps-sub is-warn">The runner on {host} updates itself to do this within a few minutes.</p>}
    </div>
  );
}
